import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * JoinFriendModal - FaceTime-style code entry for joining a friend's session
 *
 * Allows logged-in users to enter a 4-digit code without leaving TutorInterface.
 * Auto-submits when code is complete.
 *
 * FERPA/COPPA: No PII collected, just session code validation.
 */
const JoinFriendModal = ({ isOpen, onClose, user }) => {
  const [code, setCode] = useState(['', '', '', '']);
  const [isJoining, setIsJoining] = useState(false);
  const [error, setError] = useState(null);
  const inputRefs = [useRef(), useRef(), useRef(), useRef()];

  // Focus first input when modal opens
  useEffect(() => {
    if (isOpen) {
      setCode(['', '', '', '']);
      setError(null);
      setTimeout(() => inputRefs[0].current?.focus(), 100);
    }
  }, [isOpen]);

  // Auto-submit when code is complete
  useEffect(() => {
    const fullCode = code.join('');
    if (fullCode.length === 4 && code.every(d => d !== '')) {
      joinWithCode(fullCode);
    }
  }, [code]);

  const handleDigitChange = (index, value) => {
    // Only allow single digit
    const digit = value.replace(/\D/g, '').slice(-1);

    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);
    setError(null);

    // Auto-advance to next input
    if (digit && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace - go to previous input
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
    // Handle paste
    if (e.key === 'v' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then(text => {
        const digits = text.replace(/\D/g, '').slice(0, 4).split('');
        const newCode = ['', '', '', ''];
        digits.forEach((d, i) => newCode[i] = d);
        setCode(newCode);
        if (digits.length === 4) {
          inputRefs[3].current?.focus();
        } else if (digits.length > 0) {
          inputRefs[digits.length]?.current?.focus();
        }
      });
    }
  };

  const joinWithCode = async (fullCode) => {
    setIsJoining(true);
    setError(null);

    try {
      // Use logged-in user's name, fallback to 'Friend' for guests
      const friendName = user?.name || user?.username || 'Friend';

      const response = await fetch('/api/join-study-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: fullCode,
          friend_name: friendName
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Navigate to friend collaboration interface
        window.location.href = `/join/${data.session_token}`;
      } else {
        setError(data.error === 'Invalid code' ? 'Code not found' :
                 data.error === 'Code expired' ? 'Code expired' :
                 'Unable to join');
        setCode(['', '', '', '']);
        inputRefs[0].current?.focus();
      }
    } catch (err) {
      console.error('Error joining session:', err);
      setError('Connection error');
      setCode(['', '', '', '']);
    } finally {
      setIsJoining(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '20px'
        }}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#1c1c1e',
            borderRadius: '24px',
            padding: '32px',
            width: '100%',
            maxWidth: '340px',
            textAlign: 'center'
          }}
        >
          {/* Header */}
          <div style={{
            fontSize: '48px',
            marginBottom: '8px'
          }}>
            🤝
          </div>
          <h2 style={{
            color: 'white',
            fontSize: '22px',
            fontWeight: 600,
            margin: '0 0 8px 0'
          }}>
            Join Study Session
          </h2>
          <p style={{
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: '15px',
            margin: '0 0 28px 0'
          }}>
            Enter the 4-digit code from your friend
          </p>

          {/* Code Input - 4 separate boxes */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '12px',
            marginBottom: '20px'
          }}>
            {code.map((digit, index) => (
              <input
                key={index}
                ref={inputRefs[index]}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleDigitChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                disabled={isJoining}
                style={{
                  width: '56px',
                  height: '64px',
                  fontSize: '28px',
                  fontWeight: 700,
                  textAlign: 'center',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: error ? '2px solid #ff453a' : '2px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  color: 'white',
                  outline: 'none',
                  caretColor: '#297A92',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#297A92';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = error ? '#ff453a' : 'rgba(255, 255, 255, 0.2)';
                }}
              />
            ))}
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                color: '#ff453a',
                fontSize: '14px',
                marginBottom: '16px'
              }}
            >
              {error}
            </motion.div>
          )}

          {/* Loading State */}
          {isJoining && (
            <div style={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '15px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
              >
                ⏳
              </motion.div>
              Joining session...
            </div>
          )}

          {/* Cancel Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onClose}
            style={{
              width: '100%',
              padding: '14px',
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              borderRadius: '12px',
              color: 'white',
              fontSize: '16px',
              fontWeight: 500,
              cursor: 'pointer',
              marginTop: '8px'
            }}
          >
            Cancel
          </motion.button>

          {/* Help text */}
          <p style={{
            color: 'rgba(255, 255, 255, 0.4)',
            fontSize: '12px',
            marginTop: '20px'
          }}>
            Ask your friend for their study session code
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default JoinFriendModal;
